import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MapPin, CaretDown, X, MagnifyingGlass, CrosshairSimple, Circle } from "@phosphor-icons/react";
import { toast } from "sonner";
import RadiusPicker from "./RadiusPicker";
import { getGeoPosition, geoErrorMessage, getGeoDiagnostics } from "../lib/geolocation";
import { reverseGeocode as reverseGeocodeOsm } from "../lib/geo";
import { GeoProgressBar } from "./GeoProgressBar";

const UF_LIST = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
  "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
  "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

const IBGE_BASE = "https://servicodados.ibge.gov.br/api/v1/localidades";

// Cache cities by UF in memory
const cityCache = {};

// V18.9.12 — todo fetch externo tem timeout: no celular uma requisição pendurada
// deixava o botão "Detectando..." para sempre (o usuário via "nada acontecendo").
async function fetchJson(url, ms = 9000) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  try {
    const r = await fetch(url, { headers: { Accept: "application/json" }, signal: ctrl.signal });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return await r.json();
  } finally {
    clearTimeout(t);
  }
}

async function fetchCities(uf) {
  if (cityCache[uf]) return cityCache[uf];
  const data = await fetchJson(`${IBGE_BASE}/estados/${uf}/municipios`);
  cityCache[uf] = data.map((c) => c.nome).sort((a, b) => a.localeCompare(b, "pt-BR"));
  return cityCache[uf];
}

async function reverseGeocode(lat, lon) {
  try {
    const d = await fetchJson(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lon}&localityLanguage=pt`,
    );
    const city = d.city || d.locality;
    const uf = d.principalSubdivisionCode?.replace("BR-", "") || "";
    if (city && uf) return { city, uf };
  } catch (e) {
    console.warn("[CityPicker] bigdatacloud falhou:", e?.message);
  }
  // Fallback: Nominatim (mesma fonte usada no cadastro de endereço).
  try {
    const d = await reverseGeocodeOsm(lat, lon);
    if (d && d.city) return { city: d.city, uf: d.uf || "MT" };
  } catch (e) {
    console.warn("[CityPicker] nominatim falhou:", e?.message);
  }
  return null;
}

/**
 * Props:
 *  - value: { city, uf } | null
 *  - onChange: ({ city, uf }) => void
 *  - showLabel: bool
 *  - compact: bool — render only a button (used in Header)
 *  - placeholder: string
 *  - testId: string
 */
export default function CityPicker({ value, onChange, showLabel = false, compact = false, placeholder = "Selecione sua cidade", testId = "city-picker", enableRadius = false, radiusValue = null, onRadius }) {
  const [open, setOpen] = useState(false);
  const [uf, setUf] = useState(value?.uf || "MT");
  const [search, setSearch] = useState("");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [geoDebug, setGeoDebug] = useState(null);
  const [showRadius, setShowRadius] = useState(false);
  const [radiusInitial, setRadiusInitial] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetchCities(uf).then((c) => { setCities(c); setLoading(false); }).catch(() => setLoading(false));
  }, [uf, open]);

  // V18.9.18 — busca insensível a ACENTO e a caixa: digitar "cuiaba" tem que achar
  // "Cuiabá". Antes usávamos só toLowerCase e a cidade "não existia".
  const normalize = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
  const term = normalize(search);
  const filtered = term
    ? cities
        .filter((c) => normalize(c).includes(term))
        // quem começa com o termo aparece primeiro ("Cuiabá" antes de "Nova Cuiabá")
        .sort((a, b) => {
          const pa = normalize(a).startsWith(term) ? 0 : 1;
          const pb = normalize(b).startsWith(term) ? 0 : 1;
          return pa - pb || a.localeCompare(b, "pt-BR");
        })
    : cities;

  const pick = (city) => {
    onChange({ city, uf, manual: true });
    setOpen(false);
    setSearch("");
  };

  const detectLocation = async () => {
    setDetecting(true);
    setGeoDebug(null);
    try {
      const pos = await getGeoPosition({ timeout: 15000, maximumAge: 30000 });
      const r = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
      if (r && r.uf) {
        setUf(r.uf);
        // V18.9.17 — a lista de municípios do IBGE servia só para VALIDAR o nome da
        // cidade, mas era um round-trip extra que às vezes fazia o botão demorar
        // muito. Agora ela corre em paralelo com um limite de 3,5s: se não chegar,
        // aceitamos a cidade do reverse-geocode em vez de deixar o usuário esperando.
        const cs = await Promise.race([
          fetchCities(r.uf).then((l) => { setCities(l); return l; }).catch(() => null),
          new Promise((resolve) => setTimeout(() => resolve(null), 3500)),
        ]);
        if (r.city && (!cs || cs.includes(r.city))) {
          onChange({ city: r.city, uf: r.uf, lat: pos.coords.latitude, lng: pos.coords.longitude, manual: true, geo_resolved: true });
          setOpen(false);
          setSearch("");
        } else if (r.city) {
          setSearch(r.city);
        }
      } else {
        // Já temos as coordenadas — não perde a posição só porque o reverse-geocode falhou.
        onChange({ lat: pos.coords.latitude, lng: pos.coords.longitude, geo_resolved: true });
        toast.error("Pegamos seu GPS, mas não identificamos a cidade. Busque manualmente abaixo.");
      }
    } catch (e) {
      toast.error(geoErrorMessage(e));
      setGeoDebug(getGeoDiagnostics());
    } finally {
      setDetecting(false);
    }
  };

  const buttonLabel = value?.city ? `${value.city} - ${value.uf}` : placeholder;

  const openRadius = () => {
    // Centro do raio: coords já conhecidas (localização/endereço) ou geolocalização.
    const base = (radiusValue?.lat && { lat: radiusValue.lat, lng: radiusValue.lng, radius_km: radiusValue.radius_km })
      || (value?.lat && { lat: value.lat, lng: value.lng })
      || null;
    if (base) { setRadiusInitial(base); setShowRadius(true); return; }
    setDetecting(true);
    getGeoPosition({ timeout: 15000 })
      .then((p) => { setRadiusInitial({ lat: p.coords.latitude, lng: p.coords.longitude }); setShowRadius(true); })
      .catch((e) => { setRadiusInitial(null); setShowRadius(true); toast.error(geoErrorMessage(e)); })
      .finally(() => setDetecting(false));
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 text-sm font-medium hover:opacity-80"
          data-testid={testId}
        >
          <MapPin size={18} weight="fill" />
          <span className="max-w-[160px] truncate">{buttonLabel}</span>
          <CaretDown size={14} weight="bold" />
        </button>
      ) : (
        <>
          {showLabel && (
            <span className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Cidade</span>
          )}
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="aj-input mt-1 flex items-center justify-between"
            data-testid={testId}
          >
            <span className={`truncate ${value?.city ? "" : "text-slate-400"}`}>{buttonLabel}</span>
            <CaretDown size={16} weight="bold" className="text-slate-500" />
          </button>
        </>
      )}

      {open && createPortal((
        <div
          className="fixed inset-0 z-[1000] bg-black/50"
          onClick={(e) => e.target === e.currentTarget && setOpen(false)}
          data-testid={`${testId}-overlay`}
        >
          <div
            className="bg-white text-[#0F172A] pt-safe pb-safe absolute inset-x-0 top-0 bottom-0 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:max-w-md md:w-full md:rounded-2xl md:max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-heading font-bold text-lg">Selecione sua cidade</h3>
              <button onClick={() => setOpen(false)} data-testid={`${testId}-close`}>
                <X size={22} weight="bold" />
              </button>
            </div>

            <div className="p-4 space-y-3 flex-shrink-0 border-b border-slate-100">
              <button
                onClick={detectLocation}
                disabled={detecting}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border-2 border-[#004E98] text-[#004E98] font-bold text-sm hover:bg-[#004E98]/5"
                data-testid={`${testId}-detect`}
              >
                <CrosshairSimple size={16} weight="bold" />
                {detecting ? "Detectando..." : "Usar minha localização"}
              </button>

              <GeoProgressBar active={detecting} expectedMs={12000} testId={`${testId}-geo-progress`} />

              {geoDebug && (
                <details className="text-[10px] bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid={`${testId}-geo-debug`}>
                  <summary className="cursor-pointer font-bold text-slate-600">Diagnóstico da localização (para o suporte)</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all text-slate-500">{JSON.stringify(geoDebug, null, 1)}</pre>
                </details>
              )}

              {enableRadius && (
                <button
                  onClick={openRadius}
                  disabled={detecting}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-full border-2 border-[#FF6B35] text-[#FF6B35] font-bold text-sm hover:bg-[#FF6B35]/5"
                  data-testid={`${testId}-radius`}
                >
                  <Circle size={16} weight="bold" />
                  {radiusValue?.radius_km ? `Raio ativo: até ${radiusValue.radius_km} km` : "Definir raio de busca"}
                </button>
              )}

              <div className="flex gap-2">
                <select
                  value={uf}
                  onChange={(e) => setUf(e.target.value)}
                  className="aj-input flex-shrink-0 font-bold text-[#004E98]"
                  style={{ width: 90, paddingRight: "1.8rem" }}
                  data-testid={`${testId}-uf`}
                >
                  {UF_LIST.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
                <div className="relative flex-1">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Digite o nome da cidade..."
                    className="aj-input"
                    data-testid={`${testId}-search`}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    >
                      <X size={16} weight="bold" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-2" style={{ WebkitOverflowScrolling: "touch" }}>
              {loading ? (
                <div className="text-center text-slate-500 py-8 text-sm">Carregando cidades...</div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-slate-500 py-8 text-sm">Nenhuma cidade encontrada</div>
              ) : (
                <div className="space-y-1">
                  {filtered.slice(0, 200).map((c) => (
                    <button
                      key={c}
                      onClick={() => pick(c)}
                      className={`w-full text-left px-3 py-3 rounded-lg text-sm font-medium text-[#0F172A] hover:bg-slate-100 active:bg-slate-200 ${value?.city === c && value?.uf === uf ? "bg-[#004E98]/10 !text-[#004E98] font-bold" : ""}`}
                      data-testid={`${testId}-option-${c}`}
                    >
                      {c} - {uf}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ), document.body)}

      {showRadius && createPortal((
        <RadiusPicker
          initial={radiusInitial}
          onClose={() => setShowRadius(false)}
          onConfirm={(r) => {
            if (onRadius) onRadius(r);
            setShowRadius(false);
            setOpen(false);
          }}
        />
      ), document.body)}
    </>
  );
}
